import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import {
    AccomOptionDto,
    AgeGroup,
    AgeGroupResponseDto,
    DealDto,
    InventoryDto,
    InventoryResponseDto,
    PackageTypeId,
} from 'src/app/model/deal.model';
import { getApiUrlV2 } from 'src/app/utilities/url.utils';
import { CurrencyLanguageService } from 'src/app/shared/currency-language.service';
import { getTomorrow } from 'src/app/controls/calendar/calendar.utils';
import { OccupancySelection } from '@app/components/search-occupancy/search-occupancy.model';
import { sortBy } from 'lodash';

@Injectable({
    providedIn: 'root',
})
export class HotelBookingService {
    constructor(
        private httpClient: HttpClient,
        private currencyLanguageService: CurrencyLanguageService
    ) {}

    extractOccupancyIdFromOccupancySelectionAndDealAgeGroups(
        occupancySelection: OccupancySelection,
        dealAgeGroups: AgeGroup[]
    ): Observable<number> {
        const ageGroupsToSend = dealAgeGroups.map((ageGroup) => {
            if (ageGroup.ageGroupType === 'Adult') {
                return {
                    ...ageGroup,
                    qty: this.getAdultsFromOccupancySelection(
                        occupancySelection,
                        dealAgeGroups
                    ),
                };
            }
            return {
                ...ageGroup,
                qty: occupancySelection.children.reduce((acc, childAge) => {
                    if (
                        childAge >= ageGroup.ageFrom &&
                        childAge <= ageGroup.ageTo
                    ) {
                        return acc + 1;
                    }
                    return acc;
                }, 0),
            };
        });

        return this.getOccupancyId(ageGroupsToSend, 1).pipe(
            map((res) => res.id)
        );
    }

    private getAdultsFromOccupancySelection(
        occupancySelection: OccupancySelection,
        ageGroups: AgeGroup[]
    ): number {
        const adults = occupancySelection.adults;
        const adultAgeGroup = ageGroups.find(
            (ageGroup) => ageGroup.ageGroupType === 'Adult'
        );
        const childrenBilledAsAdults = occupancySelection.children.reduce(
            (acc, childAge) => {
                if (
                    childAge >= adultAgeGroup.ageFrom &&
                    childAge <= adultAgeGroup.ageTo
                ) {
                    return acc + 1;
                }
                return acc;
            },
            0
        );
        const totalAdults = adults + childrenBilledAsAdults;

        return totalAdults;
    }

    getOccupancyId(
        ageGroups: AgeGroup[],
        customerGroupId: number
    ): Observable<{ id: number }> {
        let params = new HttpParams()
            .set('orgId', 1)
            .set('customerGroupId', customerGroupId);

        ageGroups.forEach(
            (ageGroup) =>
                (params = params.set(
                    ageGroup.ageGroupType.toLowerCase(),
                    ageGroup.qty ?? 0
                ))
        );

        !params.has('teen') && (params = params.set('teen', 0));
        !params.has('child') && (params = params.set('child', 0));
        !params.has('infant') && (params = params.set('infant', 0));

        return this.httpClient.get<{ id: number }>(
            `${getApiUrlV2()}availability/occupancies/match-occupancy`,
            { params }
        );
    }

    fetchAgeGroups(deal: DealDto): Observable<AgeGroup[]> {
        if (deal.packageTypeId !== PackageTypeId.STAY) return of(null);

        return this.currencyLanguageService.getLocaleData().pipe(
            switchMap((locale) => {
                const params = new HttpParams()
                    .set('orgId', 1)
                    .set('customerGroupId', locale.customerGroupId)
                    .set('outletId', deal.outletId);

                return this.httpClient
                    .get<AgeGroupResponseDto>(
                        `${getApiUrlV2()}availability/occupancies/age-groups`,
                        { params }
                    )
                    .pipe(
                        catchError(() => of({ ageGroups: [] })),
                        map((res) => res.ageGroups),
                        map(this.processAgeGroups)
                    );
            })
        );
    }

    private processAgeGroups(ageGroups: AgeGroup[]): AgeGroup[] {
        return ageGroups
            .filter((ageGroup) => ageGroup.ageGroupType !== 'Any')
            .map((ageGroup) => {
                if (ageGroup.ageGroupType === 'Adult') {
                    ageGroup.maxPax = ageGroup.qty = 2;
                }
                return ageGroup;
            });
    }

    fetchAccomOptions(
        deal: DealDto,
        checkIn: string = getTomorrow(),
        occupancyId = 1332
    ): Observable<{ options: AccomOptionDto[] }> {
        if (deal.packageTypeId !== PackageTypeId.STAY)
            return of({ options: [] });

        return this.currencyLanguageService.getLocaleData().pipe(
            switchMap((locale) => {
                const params = new HttpParams()
                    .set('orgId', 1)
                    .set('countryCode', locale.country)
                    .set('language', locale.language)
                    .set('customerGroupId', locale.customerGroupId)
                    .set('packageTypeId', deal.packageTypeId)
                    .set('outletId', deal.outletId)
                    .set('dealId', deal.dealId)
                    .set('checkIn', checkIn)
                    .set('occupancyId', occupancyId);

                return this.httpClient
                    .get<{ options: AccomOptionDto[] }>(
                        `${getApiUrlV2()}availability/deals/pdp/accommodation/options`,
                        { params }
                    )
                    .pipe(catchError(() => of({ options: [] })));
            })
        );
    }

    fetchAccomInventory(
        deal: DealDto,
        occupancyId: number,
        mealPlanId: number
    ): Observable<{ inventory: InventoryDto[] }> {
        return this.currencyLanguageService.getLocaleData().pipe(
            switchMap((locale) => {
                const params = new HttpParams()
                    .set('orgId', 1)
                    .set('countryCode', locale.country)
                    .set('customerGroupId', locale.customerGroupId)
                    .set('packageTypeId', deal.packageTypeId)
                    .set('outletId', deal.outletId)
                    .set('dealId', deal.dealId)
                    .set('mealPlanId', mealPlanId)
                    .set('roomCrossedPercent', 0)
                    .set('occupancyId', occupancyId);

                return this.httpClient
                    .get<InventoryResponseDto>(
                        `${getApiUrlV2()}availability/deals/inventory/pdp/accommodation`,
                        { params }
                    )
                    .pipe(catchError(() => of({ inventory: [] })));
            })
        );
    }

    fetchInventoryForMealPlanAndRoom(
        deal: DealDto,
        mealPlanId: number,
        roomId: number,
        occupancyId: number
    ): Observable<{ inventory: InventoryDto[] }> {
        return this.currencyLanguageService.getLocaleData().pipe(
            switchMap((locale) => {
                const params = new HttpParams()
                    .set('orgId', 1)
                    .set('countryCode', locale.country)
                    .set('customerGroupId', locale.customerGroupId)
                    .set('packageTypeId', deal.packageTypeId)
                    .set('outletId', deal.outletId)
                    .set('dealId', deal.dealId)
                    .set('mealPlanId', mealPlanId)
                    .set('roomId', roomId)
                    .set('roomCrossedPercent', 0)
                    .set('occupancyId', occupancyId);

                return this.httpClient
                    .get<InventoryResponseDto>(
                        `${getApiUrlV2()}availability/deals/inventory/pdp/accommodation`,
                        { params }
                    )
                    .pipe(catchError(() => of({ inventory: [] })));
            })
        );
    }
}
